import { useState } from 'react';
import { useTranslation } from 'react-i18next';

export const SpecializedSectorsSection = () => {
  const [activeTab, setActiveTab] = useState('aviation');
  const { t } = useTranslation();

  const sectorData = {
    aviation: {
      items: [
        {
          icon: "fa-id-card",
          title: t('sectors.aviation.licenses.title'),
          subtitle: t('sectors.aviation.licenses.subtitle'),
          text: t('sectors.aviation.licenses.text')
        },
        {
          icon: "fa-plane-arrival",
          title: t('sectors.aviation.registries.title'),
          subtitle: t('sectors.aviation.registries.subtitle'),
          text: t('sectors.aviation.registries.text')
        },
        {
          icon: "fa-book",
          title: t('sectors.aviation.logbooks.title'),
          subtitle: t('sectors.aviation.logbooks.subtitle'),
          text: t('sectors.aviation.logbooks.text')
        },
        {
          icon: "fa-tools",
          title: t('sectors.aviation.maintenance.title'),
          subtitle: t('sectors.aviation.maintenance.subtitle'),
          text: t('sectors.aviation.maintenance.text')
        }
      ]
    },
    ports: {
      items: [
        {
          icon: "fa-file-contract",
          title: t('sectors.ports.bol.title'),
          subtitle: t('sectors.ports.bol.subtitle'),
          text: t('sectors.ports.bol.text')
        },
        {
          icon: "fa-anchor",
          title: t('sectors.ports.vessels.title'),
          subtitle: t('sectors.ports.vessels.subtitle'),
          text: t('sectors.ports.vessels.text')
        },
        {
          icon: "fa-boxes",
          title: t('sectors.ports.manifests.title'),
          subtitle: t('sectors.ports.manifests.subtitle'),
          text: t('sectors.ports.manifests.text')
        },
        {
          icon: "fa-network-wired",
          title: t('sectors.ports.gate.title'),
          subtitle: t('sectors.ports.gate.subtitle'),
          text: t('sectors.ports.gate.text')
        }
      ]
    }
  };

  return (
    <section id="especializacion" className="py-8 lg:py-12 bg-gray-50 lg:bg-white relative overflow-hidden">
      <div className="max-w-screen-2xl mx-auto px-6 sm:px-12 lg:px-20 relative z-10">

        {/* Header - Centered as requested */}
        <div className="text-center mb-10 animate-on-scroll">
          <div className="inline-block mb-4">
            <span className="font-audiowide text-xs tracking-[0.2em] text-brand-electric-blue uppercase">
              {t('sectors.badge')}
            </span>
          </div>
          <h2 className="font-audiowide text-3xl sm:text-4xl text-[#111111] mb-4">
            {t('sectors.title').split('Globales')[0]}
            <span className="relative inline-block font-audiowide">
              Globales
              <span className="absolute -bottom-1 left-0 w-full h-1 bg-gradient-to-r from-brand-electric-blue to-brand-neon-green rounded-full opacity-30"></span>
            </span>
          </h2>
          <p className="font-montserrat text-lg text-gray-500 max-w-2xl mx-auto leading-relaxed font-medium">
            {t('sectors.description')}
          </p>
        </div>

        {/* Tab Selection */}
        <div className="flex justify-center mb-8 animate-on-scroll">
          <div className="bg-gray-100 p-1 rounded-2xl flex gap-1">
            <button
              onClick={() => setActiveTab('aviation')}
              className={`px-8 py-2.5 rounded-xl font-montserrat font-bold text-sm transition-all duration-300 flex items-center gap-3 ${activeTab === 'aviation'
                  ? 'bg-white text-brand-electric-blue shadow-lg shadow-black/5'
                  : 'text-gray-400 hover:text-gray-600'
                }`}
            >
              <i className="fas fa-plane"></i>
              {t('sectors.tabs.aviation')}
            </button>
            <button
              onClick={() => setActiveTab('ports')}
              className={`px-8 py-2.5 rounded-xl font-montserrat font-bold text-sm transition-all duration-300 flex items-center gap-3 ${activeTab === 'ports'
                  ? 'bg-white text-brand-electric-blue shadow-lg shadow-black/5'
                  : 'text-gray-400 hover:text-gray-600'
                }`}
            >
              <i className="fas fa-ship"></i>
              {t('sectors.tabs.ports')}
            </button>
          </div>
        </div>

        <div className="flex flex-col lg:grid lg:grid-cols-12 gap-8 items-center">

          {/* Information Cards - Now first on mobile (order-1), but 8/12 on desktop (order-2) */}
          <div className="order-1 lg:order-2 lg:col-span-8 grid grid-cols-2 gap-3 sm:gap-4 items-start lg:pl-8">
            {sectorData[activeTab].items.map((item, index) => (
              <div
                key={`${activeTab}-${index}`}
                className="group p-4 sm:p-6 rounded-3xl border border-gray-100 bg-white hover:border-brand-electric-blue/30 transition-all duration-500 hover:shadow-2xl hover:shadow-brand-electric-blue/5 animate-fade-in"
                style={{ animationDelay: `${index * 0.1}s` }}
              >
                <div className="w-10 h-10 sm:w-12 sm:h-12 rounded-xl sm:rounded-2xl bg-brand-electric-blue/10 flex items-center justify-center text-brand-electric-blue mb-3 sm:mb-4 group-hover:bg-brand-electric-blue group-hover:text-white transition-all duration-500">
                  <i className={`fas ${item.icon} text-lg sm:text-xl`}></i>
                </div>
                <div className="text-[9px] sm:text-[10px] font-audiowide text-brand-electric-blue/60 uppercase tracking-widest mb-1">
                  {item.subtitle}
                </div>
                <h3 className="font-audiowide text-xs sm:text-base xl:text-lg text-[#111111] mb-2 group-hover:text-brand-electric-blue transition-colors duration-300">{item.title}</h3>
                <p className="font-montserrat text-[10px] sm:text-xs text-gray-500 leading-relaxed font-medium">
                  {item.text}
                </p>
              </div>
            ))}
          </div>

          {/* Illustration - Now second on mobile (order-2), but 4/12 on desktop (order-1) */}
          <div className="order-2 lg:order-1 lg:col-span-4 flex justify-center lg:justify-start animate-on-scroll mt-8 lg:mt-0">
            <div className="relative group">
              <div className="absolute inset-0 bg-brand-electric-blue/5 rounded-full blur-[80px] group-hover:bg-brand-electric-blue/10 transition-all duration-700"></div>

              <div className="relative z-10 animate-float">
                <img
                  src="/images/sectors-specialization.webp"
                  alt="Aviation and Port Specialization"
                  className="w-full max-w-[280px] sm:max-w-[320px] lg:max-w-none transform transition-transform duration-700 group-hover:rotate-2"
                  style={{ mixBlendMode: 'multiply' }}
                />
              </div>
            </div>
          </div>

        </div>

      </div>
    </section>
  );
};
